import type { Order, OrderItem, OrderRequestType, OrderSupportRequest, OrderSupportRequestItem } from "@/lib/api";
import type { SupportRequestItemPayload } from "@/lib/orderSupportRequestPayload";
import { codeSeries, mapEtapaToGradeName, mapEtapaToSegment } from "@/lib/utils";
import { formatTicketStatus, isTicketResolved } from "../OrderCancellation/cancellation";

/**
 * Regras e textos das solicitações por item de "Ajuda com o meu pedido" (troca, checklist e tipos futuros).
 * Os textos aqui são neutros de propósito: o que identifica o tipo é sempre o `title` de `GET /orders/request-types`,
 * porque o termo pode mudar e tipos novos podem ser criados só no banco.
 */

export const SUPPORT_REQUEST_DESCRIPTION_MAX_LENGTH = 2000;

// Anexos: o back valida os mesmos limites; aqui eles só evitam um envio que seria recusado.
export const ATTACHMENT_MAX_FILES = 5;
// Cada arquivo vai sozinho direto ao S3 (orderSupportRequestUpload.ts): o limite é por arquivo, o mesmo do back
// (SupportAttachments:MaxFileSizeBytes), sem teto na soma.
export const ATTACHMENT_MAX_FILE_SIZE = 20 * 1024 * 1024;
// Vídeo tem limite próprio, maior (SupportAttachments:MaxVideoFileSizeBytes no back).
export const ATTACHMENT_MAX_VIDEO_FILE_SIZE = 30 * 1024 * 1024;
const ATTACHMENT_TYPES: Record<string, string[]> = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "application/pdf": [".pdf"],
  "video/mp4": [".mp4"],
  "video/quicktime": [".mov"],
};
const ATTACHMENT_FORMATS = "JPG, PNG, PDF, MP4 ou MOV";
const toMegabytes = (bytes: number) => bytes / 1024 / 1024;
export const ATTACHMENT_ACCEPT = Object.entries(ATTACHMENT_TYPES)
  .flatMap(([mime, extensions]) => [mime, ...extensions])
  .join(",");
export const ATTACHMENT_HINT = `JPG, PNG ou PDF até ${toMegabytes(ATTACHMENT_MAX_FILE_SIZE)} MB · MP4 ou MOV até ${toMegabytes(ATTACHMENT_MAX_VIDEO_FILE_SIZE)} MB · até ${ATTACHMENT_MAX_FILES} arquivos`;

// Alguns navegadores deixam `type` vazio (ex.: arquivos arrastados de certos apps): a extensão decide.
function isAcceptedAttachment(file: File): boolean {
  if (file.type && ATTACHMENT_TYPES[file.type]) return true;
  const name = file.name.toLowerCase();
  return Object.values(ATTACHMENT_TYPES).some((extensions) => extensions.some((ext) => name.endsWith(ext)));
}

export type AttachmentKind = "image" | "video" | "pdf";

export const ATTACHMENT_KIND_LABELS: Record<AttachmentKind, string> = { image: "Imagem", video: "Vídeo", pdf: "PDF" };

/** Tipo do anexo (miniatura e rótulo): pelo tipo ou, quando o navegador não informa, pela extensão. */
export function getAttachmentKind(file: File): AttachmentKind {
  const name = file.name.toLowerCase();
  const mime =
    file.type ||
    Object.keys(ATTACHMENT_TYPES).find((type) => ATTACHMENT_TYPES[type].some((ext) => name.endsWith(ext))) ||
    "";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  return "pdf";
}

/** Limite de um arquivo: vídeo tem o seu, maior que o de foto e documento. */
export const getAttachmentMaxFileSize = (file: File) =>
  getAttachmentKind(file) === "video" ? ATTACHMENT_MAX_VIDEO_FILE_SIZE : ATTACHMENT_MAX_FILE_SIZE;

const isSameFile = (a: File, b: File) => a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

/** Junta os arquivos escolhidos aos que já estão na lista, recusando (com o motivo) o que passa dos limites. */
export function addAttachments(current: File[], incoming: File[]): { files: File[]; rejections: string[] } {
  const files = [...current];
  const rejections: string[] = [];
  let reachedLimit = false;

  for (const file of incoming) {
    if (files.some((existing) => isSameFile(existing, file))) continue;
    const maxFileSize = getAttachmentMaxFileSize(file);
    if (!isAcceptedAttachment(file)) {
      rejections.push(`${file.name}: formato não aceito. Use ${ATTACHMENT_FORMATS}.`);
    } else if (file.size > maxFileSize) {
      rejections.push(`${file.name}: maior que ${toMegabytes(maxFileSize)} MB.`);
    } else if (files.length >= ATTACHMENT_MAX_FILES) {
      reachedLimit = true;
    } else {
      files.push(file);
    }
  }

  if (reachedLimit) rejections.push(`Limite de ${ATTACHMENT_MAX_FILES} arquivos por solicitação.`);
  return { files, rejections };
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;
}

/** Código do cancelamento em request-types — usado só como identificador, nunca como texto. */
export const CANCELLATION_REQUEST_TYPE = "cancelamento";

export const supportRequestQueryKeys = {
  types: ["orderRequestTypes"] as const,
  byOrder: (orderId: string) => ["orderSupportRequests", orderId] as const,
  comments: (orderId: string, zendeskTicketId: number) =>
    ["orderSupportRequestComments", orderId, zendeskTicketId] as const,
};

export type RequestStatusTone = "inProgress" | "resolved" | "unavailable";

export interface RequestStatus {
  label: string;
  tone: RequestStatusTone;
}

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

/** `ticketStatus` nulo = Zendesk não respondeu na consulta: não é erro, só falta o status. */
export function getRequestStatus(ticketStatus: string | null | undefined): RequestStatus {
  const label = formatTicketStatus(ticketStatus);
  if (!label) return { label: "Status indisponível", tone: "unavailable" };
  return { label: capitalize(label), tone: isTicketResolved(ticketStatus) ? "resolved" : "inProgress" };
}

export const REQUEST_STATUS_BADGE_CLASSNAMES: Record<RequestStatusTone, string> = {
  inProgress:
    "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200",
  resolved: "border-primary/30 bg-primary/10 text-primary",
  unavailable: "border-dashed border-border bg-muted text-muted-foreground",
};

interface ItemRequestEligibilityInput {
  /** Status como exibido no detalhe do pedido (`getStatusLabel`): "Entregue" cobre também o código `completed`. */
  statusLabel: string;
  cancelledAt?: string | null;
  isScheduledOrder: boolean;
}

/**
 * Por que o pedido não aceita solicitação por item (troca, checklist e demais tipos com `requiresItem`);
 * `null` quando aceita. Regra de negócio do front: só pedido entregue e nunca pedido cancelado — o back não valida.
 */
export function getItemRequestBlockedReason({
  statusLabel,
  cancelledAt,
  isScheduledOrder,
}: ItemRequestEligibilityInput): string | null {
  if (cancelledAt || statusLabel === "Cancelado") return "Pedidos cancelados não aceitam esta solicitação.";
  // Programados exibem itens de scheduled_orders_items, que o POST não aceita.
  if (isScheduledOrder) return "Pedidos programados não aceitam solicitações por item por aqui.";
  if (statusLabel !== "Entregue") return "Esta solicitação só pode ser aberta depois que o pedido for entregue.";
  return null;
}

/** Item que pode ir no envio: precisa do `id` de `purchase_order_items` (`purchaseOrderItemId` do POST). */
export type SelectableOrderItem = OrderItem & { id: string };

export const getSelectableItems = (items: OrderItem[]): SelectableOrderItem[] =>
  items.filter((item): item is SelectableOrderItem => !!item.id);

/** Id do checkbox de cada item no seletor — também usado pra levar o foco ao primeiro item ao abrir o modal. */
export const getOrderItemControlId = (itemId: string) => `order-item-${itemId}`;

export const isCancellationRequest = (request: Pick<OrderSupportRequest, "requestType">) =>
  request.requestType === CANCELLATION_REQUEST_TYPE;

export type RequestListEntry =
  | { kind: "request"; request: OrderSupportRequest }
  /** Cancelamento feito na hora (pedido ainda não integrado): não abre ticket, então não vem em `/requests`. */
  | { kind: "directCancellation"; title: string; cancelledAt: string | null };

const entryTime = (entry: RequestListEntry) => {
  const date = entry.kind === "request" ? entry.request.createdAt : entry.cancelledAt;
  return (date && new Date(date).getTime()) || 0;
};

/**
 * Linhas da lista "Solicitações do pedido": o que a API devolve, mais o cancelamento direto quando houver.
 * O título dele é o do tipo `cancelamento` em request-types; sem os tipos, cai no estado do pedido.
 */
export function buildRequestEntries(
  requests: OrderSupportRequest[],
  order: Pick<Order, "cancelledAt" | "cancellationTicketId">,
  cancellationTitle?: string
): RequestListEntry[] {
  const entries: RequestListEntry[] = requests.map((request) => ({ kind: "request", request }));
  const hasDirectCancellation =
    !!order.cancelledAt && !order.cancellationTicketId && !requests.some(isCancellationRequest);
  if (!hasDirectCancellation) return entries;

  entries.push({
    kind: "directCancellation",
    title: cancellationTitle ?? "Pedido cancelado",
    cancelledAt: order.cancelledAt ?? null,
  });
  // Mesma ordem da API: mais recente primeiro.
  return entries.sort((a, b) => entryTime(b) - entryTime(a));
}

/** Diferença da conferência como no protótipo: "−1" (sinal de menos tipográfico), "+2" ou "0". */
export function formatQuantityDifference(difference: number): string {
  if (difference < 0) return `−${Math.abs(difference)}`;
  if (difference > 0) return `+${difference}`;
  return "0";
}

/** Itens da solicitação, todos no mesmo ticket; lista vazia no cancelamento (pedido inteiro). */
export const getRequestItems = (request: Pick<OrderSupportRequest, "items">) => request.items ?? [];

/** Divergência (conferência): os itens trazem a quantidade recebida. */
export const isConferenceRequest = (request: Pick<OrderSupportRequest, "items">) =>
  getRequestItems(request).some((item) => item.receivedQuantity != null);

/** Diferença recebida − pedida de um item da conferência; `null` fora da conferência. */
export function getItemDifference(item: Pick<OrderSupportRequestItem, "orderedQuantity" | "receivedQuantity">): number | null {
  if (item.receivedQuantity == null) return null;
  return item.receivedQuantity - (item.orderedQuantity ?? 0);
}

/**
 * Resumo dos itens na lista (protótipo 1d): um item mostra nome e quantidade ("· 2 un.") ou diferença ("· −1 un.");
 * vários mostram quantos são. `null` no cancelamento.
 */
export function describeRequestItems(request: Pick<OrderSupportRequest, "items">): string | null {
  const items = getRequestItems(request);
  if (items.length === 0) return null;
  if (items.length > 1) return isConferenceRequest(request) ? `${items.length} itens com diferença` : `${items.length} itens`;

  const [item] = items;
  const name = item.productDescription ?? (item.productSku ? `SKU ${item.productSku}` : "Item");
  const difference = getItemDifference(item);
  if (difference !== null) return `${name} · ${formatQuantityDifference(difference)} un.`;
  return item.quantity != null ? `${name} · ${item.quantity} un.` : name;
}

/** Quantidade recebida digitada na conferência: inteiro 0 ou mais; `null` quando não é um número válido. */
export function parseReceivedQuantity(raw: string): number | null {
  const trimmed = raw.trim();
  return /^\d+$/.test(trimmed) ? Number(trimmed) : null;
}

/** Linha de apoio do item, igual às colunas da tabela de produtos: "Segmento · Série · Uso · SKU …". */
export function describeOrderItem(item: Pick<OrderItem, "productSeries" | "productSku">, usage?: string | null): string {
  const parts = [
    mapEtapaToSegment(item.productSeries ?? null),
    mapEtapaToGradeName(item.productSeries ?? null),
    usage && usage !== "-" ? usage : null,
    item.productSku ? `SKU ${item.productSku}` : null,
  ];
  return parts
    .filter((part, index): part is string => !!part && part !== parts[index - 1])
    .join(" · ");
}

/**
 * Formulário por item, decidido pelo tipo e nunca pelo código: troca (quantidade até a pedida, descrição e anexos
 * obrigatórios) ou conferência/divergência (`capturesReceivedQuantity`: quantidade recebida obrigatória, observação
 * e anexos opcionais).
 */
export type ItemRequestVariant = "exchange" | "conference";

export const getItemRequestVariant = (type: Pick<OrderRequestType, "capturesReceivedQuantity">): ItemRequestVariant =>
  type.capturesReceivedQuantity ? "conference" : "exchange";

/**
 * Tipos com uma solicitação só por pedido: a conferência/divergência. Depois da primeira, qualquer que seja o status
 * do ticket, o portal não abre outra. O back também recusa a segunda (409).
 */
export const isSingleRequestType = (type: Pick<OrderRequestType, "capturesReceivedQuantity">) =>
  getItemRequestVariant(type) === "conference";

/** Solicitação já registrada do tipo no pedido (a mais recente, na ordem da API); `undefined` quando não há. */
export const findRequestOfType = (requests: OrderSupportRequest[], typeCode: string) =>
  requests.find((request) => request.requestType === typeCode);

export const ITEM_REQUEST_VARIANT_COPY: Record<
  ItemRequestVariant,
  {
    itemsTitle: string;
    itemsHint: string;
    quantityLabel: string;
    detailsTitle: string;
    detailsCaption: string;
    descriptionLabel: string;
    descriptionPlaceholder: string;
    descriptionRequired: boolean;
    attachmentsRequired: boolean;
  }
> = {
  exchange: {
    itemsTitle: "Quais produtos você quer trocar?",
    itemsHint: "Marque um ou mais produtos e informe a quantidade de cada um.",
    quantityLabel: "Quantidade",
    detailsTitle: "Conte o que aconteceu",
    detailsCaption: "Descrição e anexos",
    descriptionLabel: "Descrição",
    descriptionPlaceholder: "Descreva o que aconteceu",
    descriptionRequired: true,
    attachmentsRequired: true,
  },
  conference: {
    itemsTitle: "Quais produtos chegaram diferente?",
    itemsHint: "",
    quantityLabel: "Recebido",
    detailsTitle: "Observações e anexos",
    detailsCaption: "Observação e anexos",
    descriptionLabel: "Observações",
    descriptionPlaceholder: "Observações (opcional)",
    descriptionRequired: false,
    attachmentsRequired: false,
  },
};

/** Passos do formulário por item, na ordem. */
export const ITEM_REQUEST_STEPS = ["items", "details", "review"] as const;
export type ItemRequestStep = (typeof ITEM_REQUEST_STEPS)[number];

export interface OrderItemGroup<T> {
  /** Nome da série ("6º ano"), também usado como valor do item no Accordion. */
  key: string;
  /** Segmento da série ("Fund. Anos Finais"); `null` quando não acrescenta nada ao nome. */
  segment: string | null;
  items: T[];
}

/** Itens agrupados por série, na ordem escolar (Berçário, Grupo 1… 3ª série, "Outros" no fim). */
export function groupItemsBySeries<T extends Pick<OrderItem, "productSeries">>(items: T[]): OrderItemGroup<T>[] {
  const groups = new Map<string, OrderItemGroup<T>>();
  for (const item of items) {
    const key = mapEtapaToGradeName(item.productSeries ?? null);
    const group = groups.get(key);
    if (group) {
      group.items.push(item);
      continue;
    }
    const segment = mapEtapaToSegment(item.productSeries ?? null);
    groups.set(key, { key, segment: segment === key || segment === "Outros" ? null : segment, items: [item] });
  }
  // `sort` é estável: séries desconhecidas mantêm a ordem em que aparecem no pedido.
  return Array.from(groups.values()).sort((a, b) => codeSeries(a.key) - codeSeries(b.key));
}

const normalizeSearch = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

/** Busca do seletor: nome ou SKU, sem diferenciar acento nem maiúscula. */
export function matchesItemSearch(item: Pick<OrderItem, "productDescription" | "productSku">, query: string): boolean {
  const term = normalizeSearch(query);
  if (!term) return true;
  return [item.productDescription, item.productSku].some((value) => !!value && normalizeSearch(value).includes(term));
}

/** Quantidade da troca digitada: inteiro > 0 (contrato do POST); `null` quando não é um número válido. */
export function parseRequestedQuantity(raw: string): number | null {
  const trimmed = raw.trim();
  return /^\d+$/.test(trimmed) && Number(trimmed) > 0 ? Number(trimmed) : null;
}

/**
 * Erro da quantidade de um item marcado na troca; `undefined` quando está válida. Obrigatória e no máximo a
 * quantidade pedida — cada troca é avaliada sozinha, sem descontar as anteriores do mesmo item.
 */
function getRequestedQuantityError(item: Pick<OrderItem, "quantityOrdered">, raw: string): string | undefined {
  if (!raw.trim()) return "Informe a quantidade.";
  const value = parseRequestedQuantity(raw);
  if (value === null) return "Informe um número inteiro maior que 0.";
  if (value > item.quantityOrdered) return `Informe no máximo ${item.quantityOrdered}, a quantidade pedida.`;
  return undefined;
}

/** Erro da quantidade recebida de um item marcado na conferência; `undefined` quando está válida. */
function getReceivedQuantityError(item: Pick<OrderItem, "quantityOrdered">, raw: string): string | undefined {
  if (!raw.trim()) return "Informe a quantidade recebida.";
  const value = parseReceivedQuantity(raw);
  if (value === null) return "Informe um número inteiro maior ou igual a 0.";
  if (value === item.quantityOrdered) return "Igual à quantidade pedida. Desmarque o item se ele chegou certo.";
  return undefined;
}

export interface ItemsStepValidation {
  /** Erro da seleção como um todo (nenhum item marcado). */
  error?: string;
  /** Erro de cada item marcado, pelo id. */
  itemErrors: Record<string, string>;
}

/** Valida o passo dos produtos: pelo menos um item e a quantidade de cada um conforme o tipo. */
export function validateItemsStep(
  variant: ItemRequestVariant,
  selectedItems: SelectableOrderItem[],
  quantities: Record<string, string>
): ItemsStepValidation {
  if (selectedItems.length === 0) return { error: "Selecione pelo menos um item do pedido.", itemErrors: {} };

  const itemErrors: Record<string, string> = {};
  for (const item of selectedItems) {
    const raw = quantities[item.id] ?? "";
    const error =
      variant === "conference" ? getReceivedQuantityError(item, raw) : getRequestedQuantityError(item, raw);
    if (error) itemErrors[item.id] = error;
  }
  return { itemErrors };
}

export const isItemsStepValid = (validation: ItemsStepValidation) =>
  !validation.error && Object.keys(validation.itemErrors).length === 0;

/** Itens do POST na ordem recebida (a da tela), para os erros "Items[n]" da API voltarem ao item certo. */
export function buildPayloadItems(
  variant: ItemRequestVariant,
  selectedItems: SelectableOrderItem[],
  quantities: Record<string, string>
): SupportRequestItemPayload[] {
  return selectedItems.map((item) => {
    const raw = quantities[item.id] ?? "";
    if (variant === "conference") {
      return { purchaseOrderItemId: item.id, receivedQuantity: parseReceivedQuantity(raw) ?? 0 };
    }
    return { purchaseOrderItemId: item.id, quantity: parseRequestedQuantity(raw) ?? 0 };
  });
}

/** Diferença recebida − pedida enquanto o cliente digita; `null` com o campo vazio ou inválido. */
export function getReceivedDifference(item: Pick<OrderItem, "quantityOrdered">, raw: string): number | null {
  if (!raw.trim()) return null;
  const value = parseReceivedQuantity(raw);
  return value === null ? null : value - item.quantityOrdered;
}
